import { AbstractType, InjectionToken, Injector, Optional, Provider, SkipSelf, StaticClassProvider, Type, ValueProvider } from '@angular/core';

type ProviderToken<T> = Type<T> | AbstractType<T> | InjectionToken<T>

/**
 * If a parent provides this injection token, use that value. Otherwise provide our own value.
 * Not useful for modules, since parent providers take priority; but this will invert the component provider priority.
 * @param token - injection token
 * @param defaultValue - our own value to use if parent does not provide
 * @returns component or module provider
 */
export function yieldValueProvider<T>(token: ProviderToken<T>, defaultValue: T): Provider {
  return yieldProvider({ provide: token, useValue: defaultValue });
}

/**
 * If a parent provides this injection token, use that class instance. Otherwise provide our own instance.
 * Not useful for modules, since parent providers take priority; but this will invert the component provider priority.
 * @param token - injection token or class constructor
 * @param defaultClass - class constructor if different from token
 * @param deps - explicit dependency tokens to be resolved for the constructor
 * @returns component or module provider
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function yieldClassProvider<T>(token: ProviderToken<T>, defaultClass?: Type<T>, deps?: any[]): Provider {
  return yieldProvider({ provide: token, useClass: defaultClass ?? token as Type<T>, deps: deps ?? [] });
}

function yieldProvider<T>(provider: ValueProvider | StaticClassProvider): Provider {
  return {
    provide: provider.provide,
    useFactory: (parentInjector: Injector, existingDependency: T): T => {
      if (existingDependency) {
        return existingDependency;
      }
      const injector = Injector.create({ providers: [provider], parent: parentInjector });
      return injector.get(provider.provide as ProviderToken<T>);
    },
    deps: [Injector, [new Optional(), new SkipSelf(), provider.provide]],
  };
}
